import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  AlignLeft,
  ArrowLeft,
  Banknote,
  CalendarDays,
  Clock,
  MapPin,
  Pencil,
  Trash2,
  TriangleAlert,
  UserCheck,
  Users,
  Info,
  type LucideIcon,
} from "lucide-react";
import {
  getAllActivities,
  checkJoinActivity,
  joinActivity,
  deleteActivity,
  getAllActivitiesParticipants,
  acceptJoinActivity,
  rejectJoinActivity,
} from "@/services/api/activity";
import type { ActivityInfo, ActivityParticipant } from "@/services/api/activity";
import {
  formatDate,
  formatDateTime,
  formatTime,
  toImageList,
} from "@/utils/activityFormat";
import { isTruthyFlag } from "@/utils/businessCoupon";
import { getActivityCategoryLabelKey } from "@/utils/activityCategory";
import StudentEventCarousel from "@/components/student/StudentEventCarousel";
import ParticipantAvatarStack from "@/components/student/ParticipantAvatarStack";
import AdminEmptyState from "@/components/AdminEmptyState";
import AdminListSkeleton from "@/components/AdminListSkeleton";

const LOOKUP_PAGES = 5;

type DetailRow = {
  id: string;
  icon: LucideIcon;
  label: string;
  value: React.ReactNode;
};

const SectionCard: React.FC<{
  icon: LucideIcon;
  title?: string;
  children: React.ReactNode;
}> = ({ icon: Icon, title, children }) => (
  <section className="mt-4 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
    {title && (
      <h2 className="mb-3 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-gray-400">
        <Icon size={14} className="shrink-0 text-[#ffa217]" />
        {title}
      </h2>
    )}
    {children}
  </section>
);

/** -40034 ended, -40035 deadline passed, -40036 full (server flags). */
const getActivityClosedCode = (activity: ActivityInfo | null): number | null => {
  if (!activity) return null;
  if (isTruthyFlag(activity.is_activity_passed)) return -40034;
  if (isTruthyFlag(activity.is_deadline_passed)) return -40035;
  if (isTruthyFlag(activity.is_full)) return -40036;
  return null;
};

const StudentActivityDetailsPage: React.FC = () => {
  const { t } = useTranslation();
  const { activityId } = useParams<{ activityId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useSelector((state: any) => state.auth);

  const stateActivity = (location.state as { activity?: ActivityInfo } | null)?.activity;

  const [activity, setActivity] = useState<ActivityInfo | null>(stateActivity ?? null);
  const [loading, setLoading] = useState(!stateActivity);
  const [notFound, setNotFound] = useState(false);
  const [joined, setJoined] = useState(false);
  const [joinRow, setJoinRow] = useState<any>(null);
  const [participants, setParticipants] = useState<ActivityParticipant[]>([]);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [joining, setJoining] = useState(false);
  const [joinBlockCode, setJoinBlockCode] = useState<number | null>(null);

  const isOwner = activity && user && (
    activity.coordinator_id === user.id ||
    activity.business_id === user.id ||
    activity.society_id === user.id
  );

  useEffect(() => {
    if (!stateActivity) return;
    setActivity(stateActivity);
    setNotFound(false);
    setLoading(false);
  }, [stateActivity]);

  useEffect(() => {
    if (stateActivity || !activityId) return;
    let cancelled = false;
    const lookup = async () => {
      setLoading(true);
      for (let page = 1; page <= LOOKUP_PAGES; page += 1) {
        const resp = await getAllActivities(page);
        const result = resp?.result;
        if (!Array.isArray(result)) break;
        const match = result.find((item) => item.id === activityId);
        if (match) {
          if (cancelled) return;
          setActivity(match);
          setLoading(false);
          return;
        }
        if (result.length < 10) break;
      }
      if (cancelled) return;
      setLoading(false);
      setNotFound(true);
    };
    lookup();
    return () => {
      cancelled = true;
    };
  }, [stateActivity, activityId]);

  useEffect(() => {
    if (!activityId) return;
    setJoinBlockCode(null);
    const check = async () => {
      const resp = await checkJoinActivity(activityId);
      if (resp?.status === 0 && resp.result) {
        setJoined(true);
        setJoinRow(resp.result);
      } else {
        setJoined(false);
        setJoinRow(null);
      }
    };
    check();
  }, [activityId]);

  useEffect(() => {
    if (!isOwner || !activityId) return;
    const loadParticipants = async () => {
      setLoadingParticipants(true);
      const resp = await getAllActivitiesParticipants(activityId);
      setLoadingParticipants(false);
      if (resp?.status === 0 && Array.isArray(resp.result)) {
        setParticipants(resp.result);
      }
    };
    loadParticipants();
  }, [isOwner, activityId]);

  const handleJoin = async () => {
    if (!activityId || joining) return;
    if (isTruthyFlag(activity?.is_business_inactive)) {
      setJoinBlockCode(-40033);
      return;
    }
    const closedCode = getActivityClosedCode(activity);
    if (closedCode) {
      setJoinBlockCode(closedCode);
      return;
    }
    setJoining(true);
    const resp = await joinActivity(activityId, activity?.coordinator_id);
    setJoining(false);
    const status = resp?.status;
    if (status && [-40033, -40034, -40035, -40036, -40037].includes(status)) {
      setJoinBlockCode(status);
      return;
    }
    if (resp?.status === 0) {
      setJoined(true);
      const checkResp = await checkJoinActivity(activityId);
      if (checkResp?.status === 0) {
        setJoinRow(checkResp.result);
      }
    }
  };

  const handleAccept = async (joinId: string) => {
    const resp = await acceptJoinActivity(joinId);
    if (resp?.status === 0) {
      setParticipants((prev) =>
        prev.map((p) =>
          p.id === joinId
            ? { ...p, coordinator_accepted: 0, coordinator_rejected: 1 }
            : p,
        ),
      );
    }
  };

  const handleReject = async (joinId: string) => {
    const resp = await rejectJoinActivity(joinId);
    if (resp?.status === 0) {
      setParticipants((prev) =>
        prev.map((p) =>
          p.id === joinId
            ? { ...p, coordinator_accepted: 1, coordinator_rejected: 0 }
            : p,
        ),
      );
    }
  };

  const handleDelete = async () => {
    if (!activityId) return;
    if (!window.confirm(t("Are you sure you want to delete this activity?"))) return;
    const resp = await deleteActivity(activityId);
    if (resp?.status === 0) {
      navigate("/student/activities");
    }
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-6">
        <AdminListSkeleton count={3} />
      </div>
    );
  }

  if (!activity || notFound) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-10">
        <div className="rounded-2xl border border-gray-100 bg-white">
          <AdminEmptyState
            iconType="Ionicons"
            iconName="alert-circle-outline"
            title={t("Event not found")}
            message={t("This event is no longer available.")}
            actionLabel={t("Back to Home")}
            onAction={() => navigate("/student/home")}
          />
        </div>
      </div>
    );
  }

  const coverImages = toImageList(activity.cdn_activity_image);
  const activityTitle = activity.title || "";
  const activityDescription = activity.description || "";
  const dateLabel = formatDate(activity.date);
  const timeLabel = formatTime(activity.time);
  const meetingLabel = formatDateTime(
    activity.meeting_date,
    activity.meeting_time,
  );
  const deadlineLabel = formatDateTime(
    activity.deadline_date,
    activity.deadline_time,
  );
  const categoryKey = getActivityCategoryLabelKey(activity.category);
  const categoryLabel = categoryKey ? t(categoryKey) : "";
  const isBusinessInactive = isTruthyFlag(activity.is_business_inactive);
  const hasFee =
    activity.fee !== null &&
    activity.fee !== undefined &&
    String(activity.fee).trim() !== "";
  const feeLabel = hasFee
    ? Number(activity.fee) === 0
      ? t("Free")
      : `${activity.fee} ${t("HKD")}`
    : "";
  const capacityLabel =
    activity.max_participants && Number(activity.max_participants) > 0
      ? String(activity.max_participants)
      : "";
  const participantCount = Number(activity.participant_count) || 0;
  const capacityCount = Number(activity.max_participants) || 0;
  const participantPercent =
    capacityCount > 0
      ? Math.min(100, Math.round((participantCount / capacityCount) * 100))
      : 0;
  const participantsLabel = capacityLabel
    ? `${participantCount} / ${capacityLabel}`
    : participantCount > 0
      ? String(participantCount)
      : "";
  const joinAccepted =
    joinRow?.coordinator_accepted === 0 && joinRow?.coordinator_rejected === 1;
  const joinRejected =
    joinRow?.coordinator_accepted === 1 && joinRow?.coordinator_rejected === 0;
  const closedCode = getActivityClosedCode(activity);
  const isClosed = closedCode !== null;
  const blockCode = joinBlockCode ?? closedCode;
  const blockTitle =
    blockCode === -40033
      ? t("Organizer unavailable")
      : blockCode === -40034
        ? t("Activity ended")
        : blockCode === -40035
          ? t("Applications closed")
          : blockCode === -40036
            ? t("Full")
            : blockCode === -40037
              ? t("Not available")
              : null;
  const blockMessage =
    blockCode === -40033
      ? t("This activity is unavailable because the organizer is not active.")
      : blockCode === -40034
        ? t("This activity has already taken place.")
        : blockCode === -40035
          ? t("The application deadline has passed.")
          : blockCode === -40036
            ? t("This activity is full.")
            : blockCode === -40037
              ? t("Business accounts cannot join activities.")
              : null;
  const showUnavailableNotice = blockTitle !== null && blockMessage !== null;

  const keyFacts: DetailRow[] = [
    dateLabel
      ? { id: "date", icon: CalendarDays, label: t("Date"), value: dateLabel }
      : null,
    timeLabel
      ? { id: "time", icon: Clock, label: t("Time"), value: timeLabel }
      : null,
    deadlineLabel
      ? {
          id: "deadline",
          icon: Clock,
          label: t("Application Deadline"),
          value: deadlineLabel,
        }
      : null,
    meetingLabel
      ? {
          id: "meeting",
          icon: CalendarDays,
          label: t("Meeting Date & Time"),
          value: meetingLabel,
        }
      : null,
    activity.location
      ? {
          id: "location",
          icon: MapPin,
          label: t("Location"),
          value: activity.location,
        }
      : null,
    activity.meeting_location
      ? {
          id: "meeting_location",
          icon: MapPin,
          label: t("Meeting Location"),
          value: activity.meeting_location,
        }
      : null,
    feeLabel
      ? { id: "fee", icon: Banknote, label: t("Fee"), value: feeLabel }
      : null,
  ].filter(Boolean) as DetailRow[];

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 pb-16">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-gray-500 transition hover:text-[#ffa217]"
      >
        <ArrowLeft size={16} />
        {t("Back")}
      </button>

      <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        {coverImages.length > 0 ? (
          <StudentEventCarousel
            images={coverImages}
            alt={activityTitle}
            fit="natural"
          />
        ) : (
          <div className="flex h-40 w-full items-center justify-center bg-gradient-to-br from-[#fff3e0] to-gray-100">
            <CalendarDays size={36} className="text-[#ffa217]/60" />
          </div>
        )}

        <div className="p-5 sm:p-6">
          <div className="flex flex-wrap items-center gap-2">
            {isBusinessInactive && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-bold text-amber-700">
                <TriangleAlert size={12} className="shrink-0" />
                {t("Organizer unavailable")}
              </span>
            )}
            {isClosed && closedCode && (
              <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-bold text-gray-600">
                {closedCode === -40034
                  ? t("Activity ended")
                  : closedCode === -40035
                    ? t("Applications closed")
                    : t("Full")}
              </span>
            )}
            {joined && (
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold ${
                  joinAccepted
                    ? "bg-green-50 text-green-700"
                    : joinRejected
                      ? "bg-red-50 text-red-700"
                      : "bg-gray-100 text-gray-600"
                }`}
              >
                {joinAccepted ? (
                  <>
                    <UserCheck size={12} className="shrink-0" />
                    {t("Joined")}
                  </>
                ) : joinRejected ? (
                  t("Rejected")
                ) : (
                  t("Pending")
                )}
              </span>
            )}
          </div>

          <h1 className="mt-3 break-words text-2xl font-bold leading-tight text-gray-900 sm:text-3xl">
            {activityTitle || t("Untitled Event")}
          </h1>

          {categoryLabel && (
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="rounded-full bg-[#fff3e0] px-2.5 py-1 text-xs font-semibold text-[#ffa217]">
                {categoryLabel}
              </span>
            </div>
          )}
        </div>
      </div>

      {(keyFacts.length > 0 || participantsLabel) && (
        <SectionCard icon={Info} title={t("Details")}>
          {keyFacts.length > 0 && (
            <div className="grid gap-3 sm:grid-cols-2">
              {keyFacts.map((row) => {
                const Icon = row.icon;
                return (
                  <div
                    key={row.id}
                    className="flex items-start gap-2.5 rounded-xl bg-gray-50 p-3"
                  >
                    <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white text-[#ffa217] shadow-sm">
                      <Icon size={15} />
                    </span>
                    <div className="min-w-0">
                      <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                        {row.label}
                      </div>
                      <div className="mt-0.5 break-words text-sm font-medium text-gray-800">
                        {row.value}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {participantsLabel && (
            <div
              className={`flex items-start gap-2.5 rounded-xl bg-gray-50 p-3 ${
                keyFacts.length > 0 ? "mt-3" : ""
              }`}
            >
              <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white text-[#ffa217] shadow-sm">
                <Users size={15} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                    {t("Participants")}
                  </div>
                  <div className="text-sm font-bold text-gray-800">
                    {participantsLabel}
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  {participantCount > 0 ? (
                    <ParticipantAvatarStack
                      icons={activity.participant_icons}
                      names={activity.participant_names}
                      count={participantCount}
                      size={26}
                    />
                  ) : (
                    <span className="text-xs font-medium text-gray-400">
                      {t("No participants yet")}
                    </span>
                  )}
                  {capacityCount > 0 && (
                    <div className="flex min-w-[96px] flex-1 items-center gap-2">
                      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-200">
                        <div
                          className="h-full rounded-full bg-[#ffa217] transition-all"
                          style={{ width: `${participantPercent}%` }}
                        />
                      </div>
                      <span className="w-8 shrink-0 text-right text-[11px] font-semibold tabular-nums text-gray-500">
                        {participantPercent}%
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </SectionCard>
      )}

      {activityDescription && (
        <SectionCard icon={AlignLeft} title={t("Description")}>
          <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-gray-700">
            {activityDescription}
          </p>
        </SectionCard>
      )}

      {isOwner && (
        <div className="mt-4 flex flex-wrap gap-2">
          <Link
            to={`/student/activities/${activity.id}/edit`}
            className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-bold text-gray-700 transition hover:border-[#ffa217] hover:text-[#ffa217]"
          >
            <Pencil size={16} />
            {t("Edit")}
          </Link>
          <button
            type="button"
            onClick={handleDelete}
            className="inline-flex items-center gap-1.5 rounded-xl bg-red-500 px-4 py-2 text-sm font-bold text-white transition hover:bg-red-600"
          >
            <Trash2 size={16} />
            {t("Delete")}
          </button>
        </div>
      )}

      {isOwner && (
        <SectionCard icon={Users} title={t("Participants")}>
          {loadingParticipants ? (
            <AdminListSkeleton count={2} />
          ) : participants.length === 0 ? (
            <p className="text-sm text-gray-500">{t("No participants yet")}</p>
          ) : (
            <div className="space-y-2">
              {participants.map((p) => {
                const userInfo = p.user_info || {};
                const accepted =
                  p.coordinator_accepted === 0 && p.coordinator_rejected === 1;
                const rejected =
                  p.coordinator_accepted === 1 && p.coordinator_rejected === 0;
                return (
                  <div
                    key={p.id}
                    className="flex items-center justify-between rounded-xl border border-gray-100 p-3"
                  >
                    <div>
                      <div className="text-sm font-semibold text-gray-900">
                        {userInfo.username || userInfo.university_email}
                      </div>
                      <div className="text-xs text-gray-500">
                        {userInfo.university_email}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {!accepted && !rejected && (
                        <>
                          <button
                            type="button"
                            onClick={() => handleAccept(p.id)}
                            className="rounded-lg bg-green-500 px-3 py-1.5 text-xs font-bold text-white hover:bg-green-600"
                          >
                            {t("Accept")}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleReject(p.id)}
                            className="rounded-lg bg-red-500 px-3 py-1.5 text-xs font-bold text-white hover:bg-red-600"
                          >
                            {t("Reject")}
                          </button>
                        </>
                      )}
                      {accepted && (
                        <span className="text-xs font-semibold text-green-600">
                          {t("Accepted")}
                        </span>
                      )}
                      {rejected && (
                        <span className="text-xs font-semibold text-red-600">
                          {t("Rejected")}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </SectionCard>
      )}

      {!isOwner && (
        <div className="mt-4">
          {showUnavailableNotice && (
            <div className="mb-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              <TriangleAlert size={16} className="mt-0.5 shrink-0" />
              <div>
                <p className="font-bold">{blockTitle}</p>
                <p className="mt-0.5 text-xs leading-relaxed">{blockMessage}</p>
              </div>
            </div>
          )}
          <button
            type="button"
            onClick={handleJoin}
            disabled={joined || joining || isClosed}
            aria-disabled={isBusinessInactive || isClosed || joined || joining}
            className={`w-full rounded-xl px-4 py-3 text-sm font-bold transition ${
              isBusinessInactive || isClosed
                ? "cursor-not-allowed bg-gray-200 text-gray-500"
                : "bg-[#ffa217] text-white hover:bg-[#e08f0e] disabled:cursor-not-allowed disabled:opacity-60"
            }`}
          >
            {isClosed
              ? blockTitle
              : joined
                ? t("Joined")
                : joining
                  ? t("Joining...")
                  : t("Join")}
          </button>
          {joined && (
            <p
              className={`mt-2 text-center text-sm font-semibold ${
                joinAccepted
                  ? "text-green-600"
                  : joinRejected
                    ? "text-red-600"
                    : "text-gray-500"
              }`}
            >
              {joinAccepted
                ? t("Joined")
                : joinRejected
                  ? t("Rejected")
                  : t("Pending")}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default StudentActivityDetailsPage;
